import { DataListItem } from '@brighte/form-components';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { RepaymentType } from '@brighte/validation';
import {
  ErrorResponse,
  PaymentCardType,
  PaymentMethodSource,
  useCreatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  usePaymentIFrameUrlQuery,
} from '../../graphql';
import { useApplicationData, useSetApplicationData } from '../../provider';
import { KNOWN_ERROR_MESSAGES, LABELS, REPAYMENT_ACCOUNT } from '../../utils';

export function useRepaymentAccountSummary(): Array<{ title: string; items: DataListItem[] | [] }> | [] {
  const paymentDetails = useApplicationData('PaymentDetails');

  if (!paymentDetails?.payment) return [];
  const { ACCOUNT_HOLDER, SUMMARY_HEADER, BSB, ACCOUNT_NUMBER, CREDIT_CARD_NUMBER } = LABELS;
  const { TYPE, REPAYMENT_TYPE } = REPAYMENT_ACCOUNT;
  const payment = paymentDetails.payment;
  const isCreditCard = payment.type === RepaymentType.CreditCard;

  const repaymentTypeLabel = TYPE.find(option => option.value === payment.type)?.label ?? '';
  const accountHolder = isCreditCard
    ? (payment?.creditCard?.cardHolder ?? '')
    : (payment?.directDebit?.accountName ?? '');

  const accountDetailRows: DataListItem[] = isCreditCard
    ? [{ label: CREDIT_CARD_NUMBER, value: payment?.creditCard?.cardNumber }]
    : [
        { label: BSB, value: payment?.directDebit?.bsb },
        { label: ACCOUNT_NUMBER, value: payment?.directDebit?.accountNumber },
      ];

  const repaymentAccountSummary = [
    {
      title: SUMMARY_HEADER,
      // A DataList item with no value renders as a full-width standalone statement, which is right for
      // the disclaimers elsewhere but reads as a stray heading here. Every row in this summary is a
      // term/value pair, so drop any row we have no value for.
      items: [
        { label: REPAYMENT_TYPE, value: repaymentTypeLabel },
        { label: ACCOUNT_HOLDER, value: accountHolder },
        ...accountDetailRows,
      ].filter(({ value }) => !!value),
    },
  ];

  return repaymentAccountSummary;
}

export function useSavePaymentDetails() {
  const { user } = useAuth0();
  const setApplicationData = useSetApplicationData();
  const { mutate: createPaymentMethod, isLoading } = useCreatePaymentMethodMutation();

  const { mutate: deletePaymentMethod } = useDeletePaymentMethodMutation();

  const onSavePaymentDetails = async (
    formValues,
    onSuccess?: (paymentMethodId: string) => void,
    onError?: (error: unknown) => void,
  ) => {
    const payment = formValues.payment;
    const { bsb, ...accountDetails } = payment.directDebit || {};

    const debitParams = {
      paymentMethod: {
        ...accountDetails,
        accountBsb: bsb,
        source: PaymentMethodSource.CustomerWebApplication,
        userId: user?.id,
        type: payment?.type,
      },
    };
    const creditParams = {
      paymentMethod: {
        ...debitParams.paymentMethod,
        ...payment?.creditCard,
        cardType: /mastercard/i.test(payment?.creditCard?.cardType)
          ? PaymentCardType.MASTERCARD
          : payment?.creditCard?.cardType,
      },
    };

    if (payment?.methodId && user) {
      deletePaymentMethod(
        {
          userId: user.id,
          deletePaymentMethodId: payment?.methodId,
        },
        {
          onSuccess: () => {
            setApplicationData({
              PaymentDetails: {
                ...formValues,
                payment: {
                  ...formValues.payment,
                  methodId: undefined,
                },
              },
            });
          },
          onError: errorResponse => onError?.(errorResponse),
        },
      );
    }

    createPaymentMethod(payment?.type === RepaymentType.DirectDebit ? debitParams : creditParams, {
      onSuccess: data => {
        onSuccess?.(data.createPaymentMethod.id);
      },
      onError: errorResponse => {
        const errorMessage = (errorResponse as ErrorResponse)?.response?.errors?.[0].message;
        const message =
          errorMessage === REPAYMENT_ACCOUNT.EXISTING_PAYMENT_METHOD_ERROR
            ? REPAYMENT_ACCOUNT.EXISTING_PAYMENT_METHOD_ERROR_DESCRIPTION
            : KNOWN_ERROR_MESSAGES.DEFAULT;
        // Create a new error object with the updated message
        const updatedErrorResponse = {
          ...(errorResponse as ErrorResponse),
          response: {
            ...(errorResponse as ErrorResponse)?.response,
            errors: [{ message }],
          },
        };

        onError?.(updatedErrorResponse);
      },
    });
  };

  return {
    onSavePaymentDetails,
    isLoading,
  };
}

export function useGetPaymentIFrameUrl({ onSuccess }: { onSuccess: () => void }) {
  const { isFetching, data } = usePaymentIFrameUrlQuery(
    {
      source: PaymentMethodSource.Portal,
    },
    { onSuccess },
  );

  return {
    iframeUrl: data?.paymentIFrameUrl?.url,
    isLoading: isFetching,
  };
}
